import { Button, Select, TextInput, Textarea, Tooltip } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchClientTypes,
  fetchExistingEmails,
  fetchMarketSubSegments,
  fetchStates,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { UserLevelNames } from '../../Models/UserLevel';
import { HiEye } from 'react-icons/hi';
import { Vendor } from '../../Models/Vendor';
import VendorApi from '../../Api/VendorApi';

export default function VendorProfileEdit() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { states } = useAppSelector((state) => state.commonFields);
  const { userModel } = useAppSelector((state) => state.user);
  const [vendorData, setVendorData] = useState<Vendor | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [formData, setFormData] = useState<Vendor>({
    id: 0,
    name: null,
    address: null,
    city: null,
    stateId: null,
    zip: null,
    phone: null,
    email: null,
    description: null,
    website: null,
    accountNumber: null,
  });

  const getVendor = () => {
    if (id) {
      setIsLoading(true);
      VendorApi.getVendor(id)
        .then((res) => {
          setVendorData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setVendorData(null);
    }
  };

  const getReferences = async () => {
    if (!states.length) {
      await Promise.all([dispatch(fetchStates())]);
    }
  };

  useEffect(() => {
    getVendor();
  }, [id]);

  useEffect(() => {
    if (vendorData && userModel && id) {
      if (userModel.userLevelId !== UserLevelNames.Admin) {
        if (userModel.id !== vendorData.id) {
          toast.error('You do not have access to this page');
          navigate(-1);
        }
      }
    }
    handleSetInitialValues();
  }, [vendorData]);

  useEffect(() => {
    getReferences();
  }, []);

  const handleSetInitialValues = () => {
    const values = vendorData
      ? {
          ...vendorData,
        }
      : {
          id: 0,
          name: null,
          address: null,
          city: null,
          stateId: null,
          zip: null,
          phone: null,
          email: null,
          description: null,
          website: null,
          accountNumber: null,
        };

    setFormData(values as Vendor);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    if (!formData.stateId) {
      formData.stateId = null;
    }
    try {
      setIsLoading(true);
      return VendorApi.addUpdateVendor(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success('Saved');
            setIsLoading(false);
            setButtonDisabled(false);
            navigate(`/vendor/${formData?.id}`);
          } else {
            setButtonDisabled(false);
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  return (
    <div className='w-full'>
      <div className='w-full p-4'>
        <div className='mx-1 flex items-end'>
          <div className='flex flex-1'>
            <h1 className='text-gray-900 font-bold text-2xl lg:text-4xl'>
              Edit Vendor Information
            </h1>
          </div>
          <div className='right-2'>
            <Link to={`/vendor/${vendorData?.id}`}>
              <Tooltip content='View'>
                <Button className='buttonMain'>
                  <HiEye size={20} />
                </Button>
              </Tooltip>
            </Link>
          </div>
        </div>
      </div>

      <div className='h-[calc(100vh-125px)] overflow-y-auto'>
        <div className='container mx-auto p-2'>
          <div className='md:flex no-wrap'>
            <div className='w-full mx-2'>
              <div className='bg-white p-3'>
                <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8 py-2'>
                  <span className='tracking-wide uppercase text-xl lg:text-3xl'>
                    {vendorData?.name}
                  </span>
                </div>
                <hr className='bg-gray-300 h-[5px]'></hr>
                <form id='editVendor' onSubmit={onSubmit}>
                  <div className='text-gray-500'>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Name:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-full'>
                          <TextInput
                            id='name'
                            name='name'
                            type='text'
                            placeholder='Name...'
                            onChange={handleChangeInput}
                            value={formData.name ?? ''}
                            sizing='md'
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Account #:
                        </div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='accountNumber'
                          name='accountNumber'
                          type='text'
                          placeholder='Account #...'
                          onChange={handleChangeInput}
                          value={formData.accountNumber ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Email:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-full'>
                          <TextInput
                            id='email'
                            name='email'
                            type='text'
                            placeholder='Email...'
                            onChange={handleChangeInput}
                            value={formData.email ?? ''}
                            sizing='md'
                          />
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Phone:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='phone'
                          name='phone'
                          type='text'
                          placeholder='Phone...'
                          onChange={handleChangeInput}
                          value={formData.phone ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Address:</div>
                      </div>
                      <div className='flex flex-col'>
                        <div className='w-full'>
                          <TextInput
                            id='address'
                            name='address'
                            type='text'
                            placeholder='Street...'
                            onChange={handleChangeInput}
                            value={formData.address ?? ''}
                            sizing='md'
                          />
                        </div>
                        <div className='w-full flex'>
                          <div className='w-1/3'>
                            <TextInput
                              id='city'
                              name='city'
                              type='text'
                              placeholder='City...'
                              onChange={handleChangeInput}
                              value={formData.city ?? ''}
                              sizing='md'
                            />
                          </div>
                          <div className='w-1/3'>
                            <Select
                              id='stateId'
                              name='stateId'
                              onChange={handleChangeSelect}
                              value={formData.stateId ?? ''}
                              sizing='md'
                            >
                              <option>(State)</option>
                              {states.map((state) => (
                                <option key={state.id} value={state.id}>
                                  {state.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <div className='w-1/3'>
                            <TextInput
                              id='zip'
                              name='zip'
                              type='text'
                              placeholder='Zip...'
                              onChange={handleChangeInput}
                              value={formData.zip ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Website:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='website'
                          name='website'
                          type='text'
                          placeholder='Website...'
                          onChange={handleChangeInput}
                          value={formData.website ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Description:
                        </div>
                      </div>
                      <div>
                        <Textarea
                          id='description'
                          name='description'
                          placeholder='Description...'
                          onChange={handleChangeTextArea}
                          value={formData.description ?? ''}
                          rows={4}
                          autoComplete='new-password'
                        />
                      </div>
                    </div>
                  </div>
                </form>
                <div className={`flex justify-end pt-2`}>
                  <Button
                    disabled={buttonDisabled}
                    type='submit'
                    className='buttonMain'
                    form='editVendor'
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
